/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createLogger } from '@/lib/logger';

const logger = createLogger();

/**
 * Consent state derived from OneTrust's own SDK globals, mirrored here so
 * `canTrackMarketing()` has an explicit "not yet initialized" state to fail
 * closed on (as opposed to reading `window.OnetrustActiveGroups` directly,
 * which is indistinguishable between "SDK hasn't run yet" and "user has no
 * active categories").
 */
interface OneTrustConsentState {
    initialized: boolean;
    activeGroups: string[];
}

let state: OneTrustConsentState = { initialized: false, activeGroups: [] };

function parseActiveGroups(raw: string | undefined): string[] {
    return (raw ?? '')
        .split(',')
        .map((group) => group.trim())
        .filter(Boolean);
}

/**
 * Real `OptanonWrapper` handler — OneTrust calls `window.OptanonWrapper()`
 * after the SDK finishes its initial load and again after any consent
 * change. Replaces the legacy empty `function OptanonWrapper() {}` stub with
 * one that actually updates consent state, per Request A scope item 3.
 *
 * Reads `window.OnetrustActiveGroups` (set by the OneTrust SDK itself) rather
 * than any argument, matching OneTrust's documented integration contract.
 */
export function handleOptanonWrapper(): void {
    state = {
        initialized: true,
        activeGroups: parseActiveGroups(typeof window !== 'undefined' ? window.OnetrustActiveGroups : undefined),
    };
    logger.debug('OneTrust: consent state updated', { activeGroups: state.activeGroups });

    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('onetrust-consent-update', { detail: state.activeGroups }));
    }
}

/** Exposed for testing — resets the module-level consent state. */
export function resetOneTrustConsentState(): void {
    state = { initialized: false, activeGroups: [] };
}

/**
 * Whether marketing tracking (ad/marketing pixels — TikTok, Pinterest, etc.)
 * may fire right now. Fails closed (returns false) whenever consent isn't
 * affirmatively known to include the marketing category:
 * - OneTrust SDK not loaded on the page (`window.OneTrust` undefined)
 * - No marketing category configured (`config.engagement.analytics.oneTrust.marketingCategoryId`)
 * - OptanonWrapper hasn't fired yet (consent state not yet initialized)
 * - The marketing category isn't in the user's active groups
 *
 * @param marketingCategoryId - the OneTrust category ID that represents marketing
 *   consent for this org (from `config.engagement.analytics.oneTrust.marketingCategoryId`)
 */
export function canTrackMarketing(marketingCategoryId: string | undefined): boolean {
    if (typeof window === 'undefined' || typeof window.OneTrust === 'undefined') {
        logger.debug('OneTrust: blocked marketing tracking — SDK not loaded');
        return false;
    }

    if (!marketingCategoryId) {
        logger.warn('OneTrust: blocked marketing tracking — no marketingCategoryId configured');
        return false;
    }

    if (!state.initialized) {
        logger.debug('OneTrust: blocked marketing tracking — consent not yet determined');
        return false;
    }

    const allowed = state.activeGroups.includes(marketingCategoryId);
    if (allowed) {
        logger.debug('OneTrust: allowed marketing tracking', { marketingCategoryId });
    } else {
        logger.debug('OneTrust: blocked marketing tracking — category not consented', { marketingCategoryId });
    }
    return allowed;
}

/**
 * Reopens the OneTrust preference center, wrapping `OneTrust.ToggleInfoDisplay()`
 * with the same defensive `typeof` check as the legacy `OptOutDisplayBanner`
 * (see giftshop's index.js) — but implemented once here and exported as a
 * stable `window.openConsentPreferences` global (see
 * `src/components/onetrust-loader`) so it's reachable from CMS/Page
 * Designer-authored content across every storefront, not just giftshop
 * checkout.
 */
export function openConsentPreferences(): boolean {
    if (typeof window !== 'undefined' && typeof window.OneTrust !== 'undefined' && window.OneTrust) {
        window.OneTrust.ToggleInfoDisplay();
        return false;
    }
    logger.warn('OneTrust: openConsentPreferences called before OneTrust was loaded — no-op');
    return false;
}

/**
 * Registers the stable window globals OneTrust and CMS content depend on.
 * Safe to call multiple times (e.g. re-mount) — always reassigns to the
 * same function references.
 *
 * Does NOT set `window.OptanonWrapper` directly — that's defined
 * synchronously by an inline script in `OneTrustLoader` (see
 * `src/components/onetrust-loader`) so it exists at HTML-parse time, before
 * this (hydration-time) registration can run. Instead this wires the bridge
 * that inline `OptanonWrapper` calls into.
 */
export function registerOneTrustGlobals(): void {
    if (typeof window === 'undefined') return;
    window.__onetrustHandleConsentUpdate = handleOptanonWrapper;
    window.openConsentPreferences = openConsentPreferences;
    // typeof OptOutDisplayBanner !== 'undefined' ? OptOutDisplayBanner() : false
    // Should work with window. and without 
    window.OptOutDisplayBanner = openConsentPreferences;

    // Robust Event Delegation: Catch clicks on dynamically injected CMS/HTML content
    // where 'onclick' attributes might be stripped by React, sanitizers, or if the
    // element matches common classes/text for cookie/consent preferences.
    if (typeof document !== 'undefined') {
        document.addEventListener('click', (event) => {
            const target = event.target as HTMLElement | null;
            const anchor = target?.closest('a, button');
            if (!anchor) return;

            // 1. Check for legacy 'onclick' strings if React/sanitizer didn't execute it
            const rawOnClick = anchor.getAttribute('onclick');
            const hasOptOutHandler = rawOnClick && (
                rawOnClick.includes('OptOutDisplayBanner') || 
                rawOnClick.includes('openConsentPreferences')
            );

            // 2. Or check for specialized elements, CSS classes, or action-intent attributes
            const hasOptOutIntent = 
                anchor.classList.contains('optanon-show-settings') ||
                anchor.classList.contains('onetrust-show-preferences') ||
                anchor.getAttribute('data-onetrust-action') === 'preferences';

            if (hasOptOutHandler || hasOptOutIntent) {
                event.preventDefault();
                event.stopPropagation();
                openConsentPreferences();
            }
        }, { capture: true });
    }
    

    // If OptanonWrapper already fired before this ran (e.g. its inline bridge
    // was still a no-op because hydration hadn't happened yet), OneTrust will
    // already have set OnetrustActiveGroups — sync it now rather than waiting
    // for a consent change to call OptanonWrapper again. `typeof === 'string'`
    // (not just truthy) distinguishes "OneTrust already ran, no active groups"
    // (empty string) from "OneTrust hasn't run yet" (undefined).
    if (typeof window.OnetrustActiveGroups === 'string') {
        handleOptanonWrapper();
    }
}
